// UTILS
import { nextTick } from "vue"
import { storeToRefs } from "pinia"
import { setup, createActor, fromPromise } from "xstate"
import router from "@/router"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"

// TYPES
import type { MoveBisMachineEventType } from "./types"
import type { GlobalMachineEvent } from "@/machines/types"

// STORES
import { useUserStore } from "@/stores/user"
import { useI18nStore } from "@/stores/i18n"
import { useAnswersStore } from "@/features/movebis/store/answers.store"
import { useQuestionsStore } from "@/features/movebis/store/questions.store"

// MACHINE
import { globalActor } from "@/machines/globalMachine"
import { withAssessmentLoadingMinDuration } from "@/features/assessment-loading/assessmentLoadingDuration"

// Loader for MoveBis datas
// to redirect to Intro or WelcomeBack
const loadMoveBisData = fromPromise(() =>
  withAssessmentLoadingMinDuration("MOVEBIS", async () => {
    const i18nStore = useI18nStore()
    const { fetchMoveBis, retakeTest } = useAnswersStore()
    const moveBis = await fetchMoveBis()

    const { fetchQuestions, fetchAudios } = useQuestionsStore()
    const { questionsLength } = storeToRefs(useQuestionsStore())
    const { assessmentPE } = storeToRefs(useUserStore())
    const { updateLocale, isCurrentLangFr } = i18nStore

    const currentMoveBis = assessmentPE.value?.assessments?.movebis

    const assessmentSummary = useUserStore().assessmentPE.assessments.movebis
    const isFinisedAndNotPassed =
      currentMoveBis?.status === "finish" && assessmentSummary?.not_passed

    // Legacy, the test has been not_passed and considered with status finish in assessment summary (old) but not in workflow and next_assessment (new)
    let assessmentStatus = isFinisedAndNotPassed
      ? "todo"
      : currentMoveBis?.status

    // status can be "todo", "inprogress" or "finish"
    switch (assessmentStatus) {
      case "todo":
        if (isCurrentLangFr()) {
          await updateLocale(1) // Force FR
        } else {
          await updateLocale(3) // Force EN for other langs
        }

        await fetchQuestions()
        await fetchAudios()
        return { status: "new" }

      case "inprogress":
        await fetchQuestions()
        await fetchAudios()
        // If there is less answers than questions, we need to show the questions
        if (moveBis.data.items.length < questionsLength.value) {
          return { status: "showQuestions" }
        }
        // If the user has complete every steps but status is inprogress, we need to send the results to backend
        else {
          return { status: "showLoadResults" }
        }

      case "finish":
        if (currentMoveBis?.retake_test) {
          await retakeTest()
          await fetchQuestions()
          await fetchAudios()
          return { status: "new" }
        }
        return { status: "showFinal" }

      default:
        await fetchQuestions()
        await fetchAudios()
        return { status: "new" }
    }
  })
)

export const moveBisMachine = setup({
  actors: {
    loadMoveBisData,
  },
  types: {} as {
    events: MoveBisMachineEventType
  },
  actions: {},
}).createMachine({
  id: "moveBisMachine",
  initial: "loading",
  states: {
    loading: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "moveBisLoading") {
            router.replace({ name: "moveBisLoading" })
          }
        },
      ],
      invoke: {
        id: "loadMoveBis",
        src: "loadMoveBisData",
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "new",
            target: "tuto",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showQuestions",
            target: "welcomeBack",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showLoadResults",
            target: "loadResults",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showFinal",
            target: "final",
          },
        ],
      },
    },
    welcomeBack: {
      entry: [() => router.replace({ name: "moveBisTest" })],
      on: {
        done: { target: "test" },
      },
    },
    tuto: {
      entry: [() => router.replace({ name: "moveBisTuto" })],
      on: {
        done: { target: "test" },
      },
    },
    test: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "moveBisTest") {
            router.replace({ name: "moveBisTest" })
          }
        },
      ],
      on: {
        done: { target: "loadResults" },
      },
    },
    loadResults: {
      entry: [() => router.replace({ name: "moveBisLoadResults" })],
      on: {
        done: { target: "final" },
      },
    },
    final: {
      type: "final",
    },
  },
})

export let moveBisActor = createActor(moveBisMachine)

const moveBisActorObserver = {
  async complete() {
    const { fetchNextAssessment } = useUserStore()
    const { nextAssessmentName } = storeToRefs(useUserStore())

    const workflow_uuid = useCookies().get(COOKIES.WORKFLOW_UUID)
    await fetchNextAssessment(workflow_uuid ?? null)

    await nextTick()

    if (nextAssessmentName.value) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    } else {
      useCookies().remove(COOKIES.WORKFLOW_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.PM_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.COMPANY_UUID, COOKIES.OPTIONS)
      globalActor.send({ type: "GLOBAL.MOVEBIS.DONE" })
    }
  },
}

moveBisActor.subscribe(moveBisActorObserver)

/**
 * Observateurs extérieurs, réabonnés à chaque acteur neuf.
 *
 * Sans ça, un abonné qui garde la référence de l'acteur — le store d'aide, qui
 * en tire le nom de l'assessment courant, donc les CTA du header — resterait
 * branché sur l'acteur remplacé et deviendrait sourd.
 */
const moveBisActorObservers: Array<(_snapshot: unknown) => void> = []

export function subscribeToMoveBisActor(
  observer: (_snapshot: unknown) => void
) {
  moveBisActorObservers.push(observer)
  moveBisActor.subscribe(observer)
}

let hasMoveBisActorStarted = false

/**
 * XState ignore `start()` sur un acteur déjà démarré (ou arrivé à son état
 * final) : aucune action d'entrée ne rejoue, la machine ne réapplique donc
 * jamais sa route et l'utilisateur reste sur la route parente nue, écran
 * blanc. Le cas se produit dès qu'on entre deux fois dans l'assessment au
 * cours de la même page — sortie par « non passé », ou renvoi dessus par le
 * workflow. On repart donc d'un acteur neuf.
 *
 * Mais UNIQUEMENT en entrant vraiment dans l'assessment, c'est-à-dire sur la
 * route parente nue, celle par laquelle la machine globale y fait entrer. Le
 * composant racine est aussi remonté sans qu'on change d'assessment — App.vue
 * reconstruit tout `main` quand la langue change — et repartir de zéro là
 * relancerait le test depuis son écran de chargement.
 */
export function startMoveBisActor() {
  const isEnteringAssessment = router.currentRoute.value.name === "movebis"

  if (hasMoveBisActorStarted && isEnteringAssessment) {
    moveBisActor.stop()
    moveBisActor = createActor(moveBisMachine)
    moveBisActor.subscribe(moveBisActorObserver)
    for (const observer of moveBisActorObservers)
      moveBisActor.subscribe(observer)
  }

  hasMoveBisActorStarted = true
  moveBisActor.start()
}
